import type { CaptureFileJSONObject } from '@ikegps/core-api';
import { Log } from 'bblog';
import { makeObservable, observable } from 'mobx';
import { CollectionModel } from '../collection.data';
import { CollectionModelDrawable } from './drawable';
import { isBaseMarker, isBaseOffsetMarker, isMarker, isMask } from './drawable.guards';
import type { CollectionModelDrawableMarker } from './marker.drawable';
import type { CollectionModelDrawableMarkerOffset } from './marker.offset.drawable';
import type { CollectionModelDrawableMask } from './mask.drawable';

export class CollectionModelDrawableState {
	collection: CollectionModel;
	captureId: string;

	@observable drawables = observable.map<string, CollectionModelDrawable>();

	constructor(collection: CollectionModel, captureId: string) {
		makeObservable(this);

		this.collection = collection;
		this.captureId = captureId;
	}

	public clone(): CollectionModelDrawableState {
		const copy = new CollectionModelDrawableState(this.collection, this.captureId);
		for (const drawable of Array.from(this.drawables.values())) {
			drawable.clone(copy); // Cloning a drawable automatically inserts the new drawable into the new drawableState.
		}
		return copy;
	}

	get empty(): boolean {
		return this.drawables.size === 0;
	}

	/**
	 * Returns the base drawable
	 */
	get base(): CollectionModelDrawableMarker | null {
		for (const drawable of this.getAllDrawables()) {
			if (isBaseMarker(drawable)) {
				return drawable;
			}
		}

		return null;
	}

	get baseOffset(): CollectionModelDrawableMarkerOffset | null {
		for (const drawable of this.getAllDrawables()) {
			if (isBaseOffsetMarker(drawable)) {
				return drawable;
			}
		}

		return null;
	}

	get insightMask(): CollectionModelDrawableMask | null {
		return this.getAllDrawables().find((d) => isMask(d)) ?? null;
	}

	hasVisibleInsightMasks(enabledDetectionGroups: string[], log?: Log): boolean {
		for (const drawable of this.drawables.values()) {
			if (!isMask(drawable)) {
				continue;
			}
			if (drawable.getVisibleSvgDetectionGroups(enabledDetectionGroups, log)) {
				return true;
			}
		}
		return false;
	}

	clear() {
		for (const drawable of this.getAllDrawables()) {
			drawable.remove();
		}
	}

	hasDrawable(drawableId: string) {
		return this.drawables.has(drawableId);
	}

	getDrawable<T extends CollectionModelDrawable = CollectionModelDrawable>(drawableId: string): T {
		if (!this.drawables.has(drawableId)) {
			throw new Error(`Cannot find drawable (id=${drawableId}) for capture ${this.captureId}`);
		}
		const drawable = this.drawables.get(drawableId);
		if (!drawable) {
			throw new Error(
				`Cannot find drawable (id=${drawableId}) for capture ${this.captureId} -- after "has" check succeeded!`
			);
		}
		return drawable as T;
	}

	getAllDrawables(): CollectionModelDrawable[] {
		return Array.from(this.drawables.values());
	}

	values(): CollectionModelDrawable[] {
		return this.getAllDrawables();
	}

	hasMeasurements(): boolean {
		return !this.empty;
	}

	hasUserMeasurements(): boolean {
		return this.getAllDrawables().some((drawable) => drawable.isUserMeasurement);
	}

	hasOnlyInsightDetections(): boolean {
		const drawables = this.getAllDrawables();
		return drawables.length > 0 && drawables.every((drawable) => drawable.isInsightMeasurement);
	}

	hasInsightDetections(): boolean {
		return this.getAllDrawables().some((drawable) => drawable.isInsightMeasurement);
	}

	hasAppliedInsightDetections(): boolean {
		return this.getAllDrawables().some((drawable) => isMarker(drawable) && drawable.insight?.applied);
	}

	clearInsightDetections(): void {
		for (const drawable of this.getAllDrawables()) {
			if (drawable.isInsightMeasurement) {
				drawable.remove();
			}
		}
	}

	/**
	 * @deprecated Use {@link getCaptureAsync()} instead.
	 */
	get capture(): CaptureFileJSONObject | null {
		return this.collection.state.getCapture(this.captureId);
	}

	async getCaptureAsync() {
		return this.collection.state.loader.Capture.getById(this.captureId).fetch();
	}

	public equals(other: CollectionModelDrawableState): boolean {
		// This function is written to perform every comparison every time so that
		// mobx can track the dependencies of the equality process.
		let equal = true;

		if (this.captureId !== other.captureId) {
			equal = false;
		}
		const keys = Array.from(this.drawables.keys());
		const otherKeys = Array.from(other.drawables.keys());
		if (keys.length !== otherKeys.length) {
			equal = false;
		}
		if (keys.some((key) => !otherKeys.includes(key))) {
			equal = false;
		}
		if (otherKeys.some((key) => !keys.includes(key))) {
			equal = false;
		}
		for (const [drawableId, drawable] of Array.from(this.drawables.entries())) {
			const otherDrawable = other.drawables.get(drawableId);
			if (!otherDrawable) {
				equal = false;
			} else if (!drawable.equals(otherDrawable)) {
				equal = false;
			}
		}
		return equal;
	}
}
